import { useEffect, useRef, useState } from "react";
import "./index.css";

function AboutMe() {
  const [isVisible, setIsVisible] = useState(false);
  const progressRef = useRef(null);

  useEffect(() => {
    const currentRef = progressRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
          }
        });
      },
      {
        threshold: 0.3, // Trigger when 30% of the section is visible
      }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [isVisible]);

  return (
    <div className="section section--about" id="about">
      <div className="section-heading">
        <h2>ABOUT ME</h2>
      </div>

      <div className="sub-section">
        <div className="sub-section__card">
          <span>
            WHO AM <b>I</b>?
          </span>
          <div>
            <p>
              Frontend-focused Software Engineer with <b>6+ years of experience</b> building <b>scalable, 
              high-performance web applications</b> using JavaScript, React, Next.js, Preact, and Node.js 
              serving <b>millions of global users</b>.
            </p>
            <ul>
              <li>
                Experienced in <b>leading cross-functional teams</b> and <b>mentoring engineers</b> 
                to deliver high-quality products.
              </li>
              <li>
                Specialized in designing <b>micro-frontend architectures</b> and <b>component libraries</b> 
                for enterprise-scale applications.
              </li>
              <li>
                Proven track record of delivering <b>measurable business impact</b>: 
                ~30% performance improvements, ~13% mobile engagement growth.
              </li>
              <li>
                Expert in <b>code reviews</b>, establishing best practices, and 
                onboarding engineers to complex frontend architectures.
              </li>
              <li>
                Strong focus on <b>performance optimization</b>, Web Vitals, 
                and creating accessible (WCAG compliant) user interfaces.
              </li>
              <li>
                Passionate about crafting <b>reusable component systems</b> and 
                optimizing developer velocity through improved tooling.
              </li>
              <li>
                Collaborative team player working with <b>product managers, UX designers, 
                and backend teams</b> across global markets.
              </li>
              <li>
                Experience with <b>Agile/Scrum methodologies</b>, CI/CD pipelines, 
                and supporting 24×7 production systems.
              </li>
            </ul>
          </div>
        </div>
        <div className="sub-section__card">
          <span>
            EXPERTISE <b>IN</b>
          </span>
          <div>
            <p>
              As a senior frontend engineer with 6+ years of experience, I specialize in building scalable, 
              performant web applications using modern JavaScript frameworks and best practices. 
              My expertise spans the full frontend development lifecycle—from design implementation to deployment and optimization.
            </p>
            <div 
              ref={progressRef}
              className={`about--progress-items ${isVisible ? 'animate' : ''}`}
            >
              <h4>React & Next.js Ecosystem</h4>
              <div className="progress">
                <div
                  className="progress-bar progress-bar-1"
                  data-progress="90"
                >
                  <span>90%</span>
                </div>
              </div>
              <h4>Frontend System Design & Architecture</h4>
              <div className="progress">
                <div
                  className="progress-bar progress-bar-2"
                  data-progress="90"
                >
                  <span>90%</span>
                </div>
              </div>
              <h4>TypeScript & Modern JavaScript (ES6+)</h4>
              <div className="progress">
                <div
                  className="progress-bar progress-bar-3"
                  data-progress="95"
                >
                  <span>95%</span>
                </div>
              </div>
              <h4>HTML5, CSS3, Responsive Design & Accessibility</h4>
              <div className="progress">
                <div
                  className="progress-bar progress-bar-4"
                  data-progress="95"
                >
                  <span>95%</span>
                </div>
              </div>
              <h4>State Management (Redux, Zustand, Context API)</h4>
              <div className="progress">
                <div
                  className="progress-bar progress-bar-5"
                  data-progress="85"
                >
                  <span>85%</span>
                </div>
              </div>
              <h4>Performance Optimization & Web Vitals</h4>
              <div className="progress">
                <div
                  className="progress-bar progress-bar-6"
                  data-progress="85"
                >
                  <span>85%</span>
                </div>
              </div>
              <h4>Testing (Jest, React Testing Library, Cypress)</h4>
              <div className="progress">
                <div
                  className="progress-bar progress-bar-7"
                  data-progress="75"
                >
                  <span>75%</span>
                </div>
              </div>
              <h4>CI/CD, Git & DevOps Practices</h4>
              <div className="progress">
                <div
                  className="progress-bar progress-bar-8"
                  data-progress="80"
                >
                  <span>80%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="sub-section">
        <div className="sub-section__card">
          <span>
            <b>EDU</b>CATION
          </span>
          <div>
            <div>
              <table>
                <tbody>
                  <tr className="education">
                    <td>
                      <p>2013-2016</p>
                      <strong>University of Mumbai (MSBTE)</strong>
                    </td>
                    <td>
                      <h3>Diploma in Engineering in Computer Technology</h3>
                      <label>
                        Foundational computer science program covering core technologies including 
                        ASP.NET, PHP, HTML, CSS, Java, C, C++, and database fundamentals. 
                        Developed multiple projects following Agile SDLC methodologies.
                      </label>
                    </td>
                  </tr>
                  <tr className="education">
                    <td>
                      <p>2016-2019</p>
                      <strong>University of Pune</strong>
                    </td>
                    <td>
                      <h3>Bachelor of Engineering in Information Technology</h3>
                      <label>
                        Comprehensive engineering degree program focused on software development, 
                        algorithms, data structures, and modern web technologies. 
                        Completed 18+ independent projects across various tech stacks and domains.
                      </label>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div className="sub-section">
        <div className="sub-section__card">
          <span>
            <b>EX</b>PERIENCE
          </span>
          <div>
            <table>
              <tbody>
                <tr className="education">
                  <td>
                    <h3>Senior Software Engineer – Frontend</h3>
                    <p>
                      <strong>Evolutyz Corporation / Domino's Web Platforms</strong> &mdash; Jan 2023 - Present
                    </p>
                  </td>
                  <td>
                    <label>
                      <strong>Key Achievements:</strong>
                      <ul>
                        <li>
                          <p>
                            Spearheaded the <b>redesign of Domino's.co.uk web platform</b> using React and Next.js, 
                            improving site performance by <b>~30%</b> and increasing mobile engagement by <b>~13%</b>.
                          </p>
                        </li>
                        <li>
                          <p>
                            Architected and delivered a <b>micro-frontend platform</b> across US, UK, and Australian markets: 
                            decomposed legacy monolith into domain-specific frontends, improving developer velocity 
                            and reducing deploy time by <b>~25%</b>.
                          </p>
                        </li>
                        <li>
                          <p>
                            <b>Mentored a team of 5 frontend engineers and contractors</b>, instituted best-practice code reviews, 
                            and onboarded new engineers to the frontend platform architecture.
                          </p>
                        </li>
                        <li>
                          <p>
                            Collaborated closely with <b>product managers, UX designers, and backend services</b> to deliver 
                            responsive, accessible UI components ensuring cross-browser/device compatibility.
                          </p>
                        </li>
                      </ul>
                    </label>
                  </td>
                </tr>
                <tr className="education">
                  <td>
                    <h3>Associate Consultant → Lead Software Engineer – Frontend</h3>
                    <p>
                      <strong>Tata Consultancy Services (TCS)</strong> &mdash; Jul 2019 - Dec 2022
                    </p>
                  </td>
                  <td>
                    <strong>Client name - </strong> Cardinal Health Ltd. <br />
                    <strong>Domain - </strong>HealthCare
                    <br />
                    <strong>Responsibilities :</strong>
                    <div className="responsibilities">
                      <ul>
                        <li>
                          <p>
                            Development and Support through <b>agile</b>{" "}
                            approach.
                          </p>
                        </li>
                        <li>
                          <p>
                            Analyzing and creating useful requirements for
                            customer.
                          </p>
                        </li>
                        <li>
                          <p>
                            Proposing new features through <b>demos and POCs</b>
                            .
                          </p>
                        </li>
                        <li>
                          <p>
                            Writing bug free, optimized, and reusable codes.
                          </p>
                        </li>
                        <li>
                          <p>
                            Developing reusable modules, components, and
                            Services.
                          </p>
                        </li>
                        <li>
                          <p>
                            Developed <b>AngularJs</b> web app from scratch with{" "}
                            <b>.NET C#</b> backend.
                          </p>
                        </li>
                      </ul>
                      <ul>
                        <li>
                          <p>
                            Developed <b>Angular</b> web app with <b>java</b>{" "}
                            backend.
                          </p>
                        </li>
                        <li>
                          <p>
                            Developed <b>React</b> web app with <b>node.js</b>{" "}
                            backend.
                          </p>
                        </li>
                        <li>
                          <p>
                            Developed <b>Next.js</b> project with <b>node.js</b>{" "}
                            backend.
                          </p>
                        </li>
                        <li>
                          <p>
                            Developed various <b>REST APIs</b> as a
                            communication interface.
                          </p>
                        </li>
                        <li>
                          <p>
                            Writing <b>T-SQL transactions and procedures</b> in{" "}
                            <b>MSSQL</b>.
                          </p>
                        </li>
                        <li>
                          <p>
                            Writing and testing <b>jUnit</b> test cases.
                          </p>
                        </li>
                      </ul>
                    </div>
                    <strong>Achievements :</strong>
                    <ul>
                      <li>
                        <p>
                          Delivered 3+ <b>“emergency over night”</b>{" "}
                          deployments.
                        </p>
                      </li>
                      <li>
                        <p>
                          Got rewarded twice from <b>client VP and TCS DLPs</b>.
                        </p>
                      </li>
                    </ul>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AboutMe;
